import React from "react";
import moment from "moment";
import { Workspaces, Badge, DateRange } from "@mui/icons-material";
import {
  canonicalStatus,
  statusMeta,
  formatShiftDateRange,
  formatShiftDateTime,
  cleanShiftDisplayName,
  normalizeWeekOffLabel,
  scheduleRequestTypeMeta,
} from "pages/MyZone/MyZoneApply/ShiftCorrection/shiftCorrectionHelpers";

// Reuses My Zone's status helpers, so a "Rejected" status reads identically
// whether it's seen from the employee's My Requests screen or here on the
// approver's queue — one source of truth for both sides of the same request
// type. (My Zone's change-type taxonomy isn't reused here — the backend
// never populates a correction_type for either side, so there's nothing to
// label.)
export { canonicalStatus, statusMeta, formatShiftDateRange };

export const formatScheduleDateDisplay = (record = {}) =>
  record.request_type === "week_off_swap" && record.date && record.swap_date
    ? `${moment(record.date).format("DD MMM YYYY")} <-> ${moment(record.swap_date).format("DD MMM YYYY")}`
    : formatShiftDateRange(record.date);

// Team Zone's own column set (Name/Employee ID ahead of the request columns
// My Zone already defined). Colocated here rather than in the shared
// TeamZone/Approve/Headers.js since this screen's fields (assigned/
// requested shift, no leave-style approval metadata) don't match that
// file's shape.
export const SHIFT_CORRECTION_HEADERS = [
  {
    key: "employee_name",
    name: "Name",
    type: "string",
    icon: <Workspaces fontSize="small" />,
  },
  {
    key: "employee_id",
    name: "Employee ID",
    type: "string",
    icon: <Badge fontSize="small" />,
  },
  { key: "request_type_label", name: "Type", type: "string" },
  {
    key: "date_display",
    name: "Date",
    type: "date",
    icon: <DateRange fontSize="small" />,
  },
  { key: "original_shift", name: "Original Shift", type: "string" },
  { key: "corrected_shift", name: "New Shift", type: "string" },
  { key: "approver_name", name: "Approver", type: "string" },
  { key: "status", name: "Status", type: "autocomplete" },
];

// Normalizes a record for CustomFilter's search/sort and the table — every
// visible column must address a flat row[key].
export const toApproveFilterRow = (r) => ({
  ...r,
  employee_name: cleanShiftDisplayName(r.employee_name, r.employee_id),
  request_type_label: scheduleRequestTypeMeta(r.request_type).label,
  date_display: formatScheduleDateDisplay(r),
  // getStatusFlow (services/constants) already reads these same fields for
  // the expand row's approval timeline — one column showing whichever of
  // the three actually fired for this row.
  applied_by: cleanShiftDisplayName(r.applied_by, r.employee_id),
  approved_by: cleanShiftDisplayName(r.approved_by, r.approver_id),
  rejected_by: cleanShiftDisplayName(r.rejected_by, r.rejected_by_id),
  cancelled_by: cleanShiftDisplayName(r.cancelled_by, r.cancelled_by_id),
  approver_name:
    cleanShiftDisplayName(r.approved_by, r.approver_id) ||
    cleanShiftDisplayName(r.rejected_by, r.rejected_by_id) ||
    cleanShiftDisplayName(r.cancelled_by, r.cancelled_by_id) ||
    "-",
});

// Same date-format tolerance every other real Approve screen's toISODate
// uses — team_zone endpoints don't guarantee ISO on the way back.
const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

// applied_on/approved_at come back as "DD-MM-YYYY, HH:mm" (confirmed from a
// live GET response) — moment can't auto-detect that shape, so it needs an
// explicit parse format rather than the ISO-guessing `moment(value)` other
// screens get away with.
export const formatAppliedDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value, "DD-MM-YYYY, HH:mm", true);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

// Backend endpoint:
//   GET team_zone/approve/shift_change/teamzone-shift-change/?user_id=<manager id>&company_id=<id>&start_date=&end_date=
// Confirmed from a live response: one record per date (a POST'd date range
// gets split into a row per day server-side), so there's no to_date/end_date
// to read here — just `date`. Beyond that it's a superset of My Zone's
// toShiftChangeRecord (MyZoneApply/ShiftCorrection/shiftCorrectionHelpers.js)
// shape (assigned_shift/requested_shift as display names, no correction_type
// since the model doesn't track one) plus the approve-side fields every
// team_zone Approve row carries — employee_id/employee_code/department/
// designation/color_code and the applied_by/applied_on/approved_by/
// approved_at quartet the shared getStatusFlow() timeline (services/
// constants) already reads straight off the record via the `...r` spread
// below.
export const toApproveShiftChangeRecord = (r = {}) => ({
  ...r,
  id: r.id,
  request_id: r.id ? `SCR-${String(r.id).padStart(4, "0")}` : undefined,
  request_type: r.request_type || "shift_change",
  date: toISODate(r.date),
  swap_date: toISODate(r.swap_date),
  employee_name: cleanShiftDisplayName(r.employee_name, r.employee_id),
  applied_by: cleanShiftDisplayName(r.applied_by, r.employee_id),
  approved_by: cleanShiftDisplayName(r.approved_by, r.approver_id),
  rejected_by: cleanShiftDisplayName(r.rejected_by, r.rejected_by_id),
  cancelled_by: cleanShiftDisplayName(r.cancelled_by, r.cancelled_by_id),
  original_shift:
    (r.request_type || "shift_change") === "week_off_swap"
      ? "Week Off"
      : normalizeWeekOffLabel(r.assigned_shift) || "—",
  corrected_shift:
    (r.request_type || "shift_change") === "week_off_swap"
      ? normalizeWeekOffLabel(r.requested_shift || r.assigned_shift) || "Working Day"
      : normalizeWeekOffLabel(r.requested_shift) || "—",
  applied_display: formatShiftDateTime(r.applied_on),
  reason: r.reason,
  status: r.status,
  rejected_reason: r.rejected_reason,
});
